// src/pages/SalesPlanning.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, FormGroup, FormControlLabel, Checkbox, Button, GlobalStyles, MenuItem, Select
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import api from '../api';
import { formatErrorMessage } from '../utils';
import { getConfig } from '../config';

// ====== Types ======
const { COMPANY_NAME } = getConfig();

interface Payment { id: number; amount: number; due_date: string; }
interface SaleItem {
  id: number; item: string; amount: number;
  release_date?: string; state?: string; completion_date?: string; payments?: Payment[];
}
interface Sale {
  id: number;
  contract_date: string;
  company: { id: number; name: string };
  site: { id: number; name: string };
  total_amount: number;
  items: SaleItem[];
}

type ViewMode = 'YEAR' | 'RANGE';
const months = Array.from({ length: 12 }, (_, i) => i + 1);
const fmt = (n: number) => (n ?? 0).toLocaleString();

// ====== Component ======
const SalesPlanning: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // 보기 모드
  const [mode, setMode] = useState<ViewMode>('YEAR');

  // 연도별
  const thisYear = new Date().getFullYear();
  const [displayYear, setDisplayYear] = useState<number>(thisYear);
  const [selectedMonths, setSelectedMonths] = useState<number[]>(months);

  // 기간별
  const [rangeStartYear, setRangeStartYear] = useState<number>(thisYear);
  const [rangeStartMonth, setRangeStartMonth] = useState<number>(new Date().getMonth() + 1);
  const [rangeLen, setRangeLen] = useState<number>(3); // 3개월 등

  const printAreaRef = useRef<HTMLDivElement>(null);

  // ====== 접힘 상태(백엔드 저장/로드) ======
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // 초기 로드: 레이아웃 불러오기
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/user-layout/sales-planning');
        setCollapsed(data?.collapsed || {});
      } catch (e) {
        console.warn('layout fetch failed', e);
      }
    })();
  }, []);

  // 저장 유틸(실패 시 스낵바만)
  const saveLayout = async (next: Record<string, boolean>) => {
    try {
      await api.post('/api/user-layout/sales-planning', { collapsed: next });
    } catch (e) {
      enqueueSnackbar('표시 설정 저장에 실패했습니다.', { variant: 'warning' });
      console.error(e);
    }
  };

  // group key: (연도 + 업체명) 기준
  const groupKey = (year: number, companyName: string) => `${year}__${companyName}`;

  // 토글(낙관적 업데이트 + 서버 저장)
  const toggleGroup = (key: string) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveLayout(next);
      return next;
    });
  };

  // ====== Fetch ======
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/sales');
        const sorted: Sale[] = data.sort(
          (a: Sale, b: Sale) => new Date(a.contract_date).getTime() - new Date(b.contract_date).getTime()
        );
        setSales(sorted);
      } catch (error) {
        enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  // ====== Print styles ======
  const printStyles = (
    <GlobalStyles styles={{
      '@media print': {
        '.collapsed-placeholder': { display: 'none !important' },
        body: { margin: 0, padding: 0, backgroundColor: '#fff' },
        '*': { boxSizing: 'border-box' },
        '#root > *': { display: 'none' },
        '#print-area': { display: 'block !important', position: 'static' },
      }
    }} />
  );

  // ====== Money utils ======
  const getPlannedMonth = (d?: string) => {
    if (!d) return null;
    const date = new Date(d);
    return date.getDate() <= 15 ? date.getMonth() + 1 : (date.getMonth() + 2) % 12 || 12;
  };

  const plannedYM = (item: SaleItem, y: number, m: number) => {
    let t = 0;
    for (const p of item.payments || []) {
      const d = new Date(p.due_date);
      if (d.getFullYear() === y && d.getMonth() + 1 === m) t += p.amount;
    }
    const paid = (item.payments || []).reduce((s, p) => s + p.amount, 0);
    const remaining = item.amount - paid;
    if (remaining > 0 && item.release_date) {
      const rd = new Date(item.release_date);
      const pm = getPlannedMonth(item.release_date);
      if (pm === m && rd.getFullYear() === y) t += remaining;
    }
    return t;
  };

  const actualYM = (item: SaleItem, y: number, m: number) => {
    let t = 0;
    for (const p of item.payments || []) {
      const d = new Date(p.due_date);
      if (d.getFullYear() === y && d.getMonth() + 1 === m) t += p.amount;
    }
    const paid = (item.payments || []).reduce((s, p) => s + p.amount, 0);
    const remaining = item.amount - paid;
    if (remaining > 0 && item.completion_date) {
      const cd = new Date(item.completion_date);
      if (cd.getFullYear() === y && cd.getMonth() + 1 === m) t += remaining;
    }
    return t;
  };

  const actualUpTo = (item: SaleItem, cutoff: Date) => {
    let t = 0;
    for (const p of item.payments || []) {
      const d = new Date(p.due_date);
      if (d <= cutoff) t += p.amount;
    }
    const paid = (item.payments || []).reduce((s, p) => s + p.amount, 0);
    const remaining = item.amount - paid;
    if (remaining > 0 && item.completion_date) {
      const cd = new Date(item.completion_date);
      if (cd <= cutoff) t += remaining;
    }
    return t;
  };

  // ====== YEAR view ======
  const yearView = useMemo(() => {
    type Row = {
      contractDate: string;
      contractYear: number;
      company: string; site: string;
      total: number;
      prevCum: number;  // 전년도 누계(해당년도 시작 전까지)
      yearCum: number;  // 해당년도 누계(해당년도 내 실적 합)
      remain: number;
      planned: number[]; actuals: number[];
    };
    const map = new Map<string, Row>(); // key: `${contractYear}__${company}__${site}`

    const prevYearEnd = new Date(displayYear - 1, 11, 31);
    const yearEnd = new Date(displayYear, 11, 31);

    sales.forEach(sale => {
      const cy = new Date(sale.contract_date).getFullYear();
      const key = `${cy}__${sale.company.name}__${sale.site.name}`;

      const plannedArr = months.map(m => sale.items.reduce((s, it) => s + plannedYM(it, displayYear, m), 0));
      const actualArr = months.map(m => sale.items.reduce((s, it) => s + actualYM(it, displayYear, m), 0));

      const prevCum = sale.items.reduce((s, it) => s + actualUpTo(it, prevYearEnd), 0);
      const yearCum = actualArr.reduce((s, v) => s + v, 0);
      const uptoYearEnd = sale.items.reduce((s, it) => s + actualUpTo(it, yearEnd), 0);
      const remain = sale.total_amount - uptoYearEnd;

      const ex = map.get(key);
      if (ex) {
        ex.total += sale.total_amount;
        ex.prevCum += prevCum;
        ex.yearCum += yearCum;
        ex.remain += remain;
        ex.planned = ex.planned.map((v, i) => v + plannedArr[i]);
        ex.actuals = ex.actuals.map((v, i) => v + actualArr[i]);
        if (new Date(sale.contract_date) < new Date(ex.contractDate)) ex.contractDate = sale.contract_date;
      } else {
        map.set(key, {
          contractDate: sale.contract_date,
          contractYear: cy,
          company: sale.company.name,
          site: sale.site.name,
          total: sale.total_amount,
          prevCum, yearCum, remain,
          planned: plannedArr,
          actuals: actualArr
        });
      }
    });

    const rows = Array.from(map.values()).sort(
      (a, b) => new Date(a.contractDate).getTime() - new Date(b.contractDate).getTime()
    );

    type CompanyGroup = { company: string; rows: Row[]; };
    type YearSection = {
      year: number; companies: CompanyGroup[]; totals: {
        total: number; prevCum: number; yearCum: number; remain: number; planned: number[]; actuals: number[];
      }
    };
    const sectionsMap = new Map<number, YearSection>();
    rows.forEach(r => {
      if (!sectionsMap.has(r.contractYear)) {
        sectionsMap.set(r.contractYear, {
          year: r.contractYear,
          companies: [],
          totals: { total: 0, prevCum: 0, yearCum: 0, remain: 0, planned: Array(12).fill(0), actuals: Array(12).fill(0) }
        });
      }
      const sec = sectionsMap.get(r.contractYear)!;
      let cg = sec.companies.find(c => c.company === r.company);
      if (!cg) { cg = { company: r.company, rows: [] }; sec.companies.push(cg); }
      cg.rows.push(r);

      sec.totals.total += r.total;
      sec.totals.prevCum += r.prevCum;
      sec.totals.yearCum += r.yearCum;
      sec.totals.remain += r.remain;
      for (let i = 0; i < 12; i++) {
        sec.totals.planned[i] += r.planned[i];
        sec.totals.actuals[i] += r.actuals[i];
      }
    });

    const sections = Array.from(sectionsMap.values()).sort((a, b) => a.year - b.year);

    const grand = {
      total: sections.reduce((s, sec) => s + sec.totals.total, 0),
      prevCum: sections.reduce((s, sec) => s + sec.totals.prevCum, 0),
      yearCum: sections.reduce((s, sec) => s + sec.totals.yearCum, 0),
      remain: sections.reduce((s, sec) => s + sec.totals.remain, 0),
      planned: months.map((_, i) => sections.reduce((s, sec) => s + (sec.totals.planned[i] || 0), 0)),
      actuals: months.map((_, i) => sections.reduce((s, sec) => s + (sec.totals.actuals[i] || 0), 0)),
    };

    return { sections, grand };
  }, [sales, displayYear]);

  // ====== RANGE view ======
  const rangeView = useMemo(() => {
    type YM = { year: number; month: number; label: string };
    const buildRange = (y: number, m: number, len: number): YM[] => {
      const arr: YM[] = [];
      let yy = y, mm = m;
      for (let i = 0; i < len; i++) {
        arr.push({ year: yy, month: mm, label: `${yy}-${String(mm).padStart(2, '0')}` });
        mm += 1;
        if (mm > 12) { mm = 1; yy += 1; }
      }
      return arr;
    };

    const rms = buildRange(rangeStartYear, rangeStartMonth, rangeLen);
    const endDate = new Date(rms[rms.length - 1].year, rms[rms.length - 1].month, 0);

    type Row = {
      contractDate: string;
      company: string; site: string;
      total: number; actualInRange: number; remainAtEnd: number;
      planned: number[]; actuals: number[];
    };

    type CompanyGroup = {
      company: string; rows: Row[]; subtotal: {
        total: number; actual: number; remain: number; planned: number[]; actuals: number[];
      }
    };
    type YearSection = {
      year: number; months: number[]; companies: CompanyGroup[]; totals: {
        total: number; actual: number; remain: number; planned: number[]; actuals: number[];
      }
    };

    const perYearGroupMap = new Map<number, Map<String, Row>>();

    sales.forEach(sale => {
      const cy = new Date(sale.contract_date).getFullYear();
      if (!perYearGroupMap.has(cy)) perYearGroupMap.set(cy, new Map<String, Row>());
      const m = perYearGroupMap.get(cy)!;
      const key = `${sale.company.name}__${sale.site.name}`;

      const plannedArr = rms.map(({ year, month }) =>
        sale.items.reduce((s, it) => s + plannedYM(it, year, month), 0)
      );
      const actualArr = rms.map(({ year, month }) =>
        sale.items.reduce((s, it) => s + actualYM(it, year, month), 0)
      );

      const actualInRange = actualArr.reduce((s, v) => s + v, 0);
      const uptoEnd = sale.items.reduce((s, it) => s + actualUpTo(it, endDate), 0);
      const remainAtEnd = sale.total_amount - uptoEnd;

      const ex = m.get(key);
      if (ex) {
        ex.total += sale.total_amount;
        ex.actualInRange += actualInRange;
        ex.remainAtEnd += remainAtEnd;
        ex.planned = ex.planned.map((v, i) => v + plannedArr[i]);
        ex.actuals = ex.actuals.map((v, i) => v + actualArr[i]);
        if (new Date(sale.contract_date) < new Date(ex.contractDate)) ex.contractDate = sale.contract_date;
      } else {
        m.set(key, {
          contractDate: sale.contract_date,
          company: sale.company.name,
          site: sale.site.name,
          total: sale.total_amount,
          actualInRange, remainAtEnd,
          planned: plannedArr,
          actuals: actualArr
        });
      }
    });

    const sections: YearSection[] = [];
    Array.from(perYearGroupMap.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([year, rowsMap]) => {
        const sec: YearSection = {
          year,
          months: rms.map(r => r.month),
          companies: [],
          totals: {
            total: 0, actual: 0, remain: 0,
            planned: Array(rms.length).fill(0),
            actuals: Array(rms.length).fill(0)
          }
        };

        const companyMap = new Map<string, CompanyGroup>();
        const rows = Array.from(rowsMap.values()).sort(
          (a, b) => new Date(a.contractDate).getTime() - new Date(b.contractDate).getTime()
        );

        rows.forEach(r => {
          if (!companyMap.has(r.company)) {
            companyMap.set(r.company, {
              company: r.company,
              rows: [],
              subtotal: {
                total: 0, actual: 0, remain: 0,
                planned: Array(rms.length).fill(0),
                actuals: Array(rms.length).fill(0)
              }
            });
          }
          const cg = companyMap.get(r.company)!;
          cg.rows.push(r);

          cg.subtotal.total += r.total;
          cg.subtotal.actual += r.actualInRange;
          cg.subtotal.remain += r.remainAtEnd;
          for (let i = 0; i < rms.length; i++) {
            cg.subtotal.planned[i] += r.planned[i] || 0;
            cg.subtotal.actuals[i] += r.actuals[i] || 0;
          }

          sec.totals.total += r.total;
          sec.totals.actual += r.actualInRange;
          sec.totals.remain += r.remainAtEnd;
          for (let i = 0; i < rms.length; i++) {
            sec.totals.planned[i] += r.planned[i] || 0;
            sec.totals.actuals[i] += r.actuals[i] || 0;
          }
        });

        sec.companies = Array.from(companyMap.values());
        sections.push(sec);
      });

    const grand = {
      total: sections.reduce((s, sec) => s + sec.totals.total, 0),
      actual: sections.reduce((s, sec) => s + sec.totals.actual, 0),
      remain: sections.reduce((s, sec) => s + sec.totals.remain, 0),
    };

    return { rms, sections, grand };
  }, [sales, rangeStartYear, rangeStartMonth, rangeLen]);

  // ====== Print ======
  const titleText = mode === 'YEAR'
    ? `${displayYear}년`
    : `${rangeView.rms[0].label} ~ ${rangeView.rms[rangeView.rms.length - 1].label}`;

  const handlePrint = () => {
    const html = printAreaRef.current?.innerHTML;
    const head = Array.from(document.head.children)
      .filter((el) => el.tagName === 'STYLE' || el.tagName === 'LINK')
      .map((el) => (el as HTMLElement).outerHTML).join('\n');
    if (!html) return;
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head>${head}
<style>
*{ -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; }
body{ font-family:Arial; margin:0; padding:10px; }
table{ border-collapse:collapse; width:100%; }
th,td{ border:1px solid #000; padding:4px; text-align:center; font-size:12px; }
th{ background:#eee !important; }
.row-subtotal{ background:#eee !important; }
.row-year{ background:#ddd !important; font-weight:700; }
.row-grand{ background:#ffe599 !important; font-weight:700; }
.collapsed-placeholder{ display:none !important; }
</style></head><body>
<h2 style="text-align:center">${COMPANY_NAME} ${titleText} 매출계획</h2>
<div style="text-align:right; margin-bottom:10px;">${new Date().toLocaleDateString()} (단위: 1000원)</div>
${html}
</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  // ====== 전체 접기/펼치기용 모든 그룹 키 수집 ======
  const allGroupKeys = useMemo(() => {
    if (mode === 'YEAR') {
      return yearView.sections.flatMap(sec => sec.companies.map(cg => groupKey(sec.year, cg.company)));
    }
    return rangeView.sections.flatMap(sec => sec.companies.map(cg => groupKey(sec.year, cg.company)));
  }, [mode, yearView, rangeView]);

  const collapseAll = () => {
    const obj: Record<string, boolean> = {};
    allGroupKeys.forEach(k => { obj[k] = true; });
    setCollapsed(obj);
    saveLayout(obj);
  };
  const expandAll = () => {
    setCollapsed({});
    saveLayout({});
  };

  // 고정 컬럼 개수: YEAR=7 (계약일/업체명/현장명/계약금액/전년도누계/해당년도누계/잔액), RANGE=6
  const pairCount = mode === 'YEAR' ? selectedMonths.length : rangeView.rms.length;
  const baseCols = mode === 'YEAR' ? 7 : 6;
  const totalColSpan = baseCols + pairCount * 2;

  // ====== Render ======
  return (
    <>
      {printStyles}
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, alignItems: 'center' }}>
          <Typography variant="h5">{COMPANY_NAME} {titleText} 매출계획</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Select size="small" value={mode} onChange={(e) => setMode(e.target.value as ViewMode)}>
              <MenuItem value="YEAR">연도별</MenuItem>
              <MenuItem value="RANGE">기간(월~월)</MenuItem>
            </Select>

            {mode === 'YEAR' ? (
              <Select size="small" value={displayYear} onChange={(e) => setDisplayYear(Number(e.target.value))}>
                {Array.from(new Set([thisYear - 1, thisYear, thisYear + 1, ...sales.map(s => new Date(s.contract_date).getFullYear())]))
                  .sort((a, b) => a - b)
                  .map(y => <MenuItem key={y} value={y}>{y}년</MenuItem>)}
              </Select>
            ) : (
              <>
                <Select size="small" value={rangeStartYear} onChange={(e) => setRangeStartYear(Number(e.target.value))}>
                  {[thisYear - 1, thisYear, thisYear + 1, thisYear + 2].map(y => <MenuItem key={y} value={y}>{y}년</MenuItem>)}
                </Select>
                <Select size="small" value={rangeStartMonth} onChange={(e) => setRangeStartMonth(Number(e.target.value))}>
                  {months.map(m => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
                </Select>
                <Select size="small" value={rangeLen} onChange={(e) => setRangeLen(Number(e.target.value))}>
                  {[1, 2, 3, 4, 5, 6, 9, 12].map(n => <MenuItem key={n} value={n}>{n}개월</MenuItem>)}
                </Select>
              </>
            )}

            <Button size="small" variant="outlined" onClick={collapseAll}>전체 접기</Button>
            <Button size="small" variant="outlined" onClick={expandAll}>전체 펼치기</Button>

            <Button variant="outlined" startIcon={<DownloadIcon />}>CSV 다운로드</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>프린트</Button>
          </Box>
        </Box>

        {/* Month filter (YEAR only) */}
        {mode === 'YEAR' && (
          <FormGroup row sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={selectedMonths.length === 12} onChange={(e) => setSelectedMonths(e.target.checked ? months : [])} />}
              label="전체 선택"
            />
            {months.map(m => (
              <FormControlLabel
                key={m}
                control={<Checkbox
                  checked={selectedMonths.includes(m)}
                  onChange={() => setSelectedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
                />}
                label={`${m}월`}
              />
            ))}
          </FormGroup>
        )}

        {/* Table */}
        <Box ref={printAreaRef} id="print-area">
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& td, & th': { border: '1px solid #ccc', padding: '6px' }, '& tr': { height: '36px' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>계약일</TableCell>
                  <TableCell>업체명</TableCell>
                  <TableCell>현장명</TableCell>
                  <TableCell>계약금액</TableCell>

                  {/* YEAR 모드: 전년도 누계 / 해당년도 누계 / 수주잔액 */}
                  {mode === 'YEAR' ? (
                    <>
                      <TableCell>{`${displayYear - 1} 누계`}</TableCell>
                      <TableCell>{`${displayYear} 누계`}</TableCell>
                      <TableCell>수주잔액</TableCell>
                    </>
                  ) : (
                    <>
                      {/* RANGE 모드: 기간 누계 / 기간 종료 잔액 */}
                      <TableCell>기간 누계</TableCell>
                      <TableCell>기간 종료 잔액</TableCell>
                    </>
                  )}

                  {mode === 'YEAR'
                    ? selectedMonths.map(m => <TableCell key={m} colSpan={2} align="center">{m}월</TableCell>)
                    : rangeView.rms.map(rm => <TableCell key={rm.label} colSpan={2} align="center">{rm.label}</TableCell>)
                  }
                </TableRow>
                <TableRow>
                  <TableCell colSpan={baseCols} />
                  {(mode === 'YEAR' ? selectedMonths : rangeView.rms).map((_, i) => (
                    <React.Fragment key={`subh-${i}`}>
                      <TableCell align="center" sx={{ background: '#f9f9f9' }}>계획</TableCell>
                      <TableCell align="center" sx={{ background: '#f9f9f9' }}>실적</TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* YEAR MODE */}
                {mode === 'YEAR' && (
                  <>
                    {yearView.sections.map(sec => (
                      <React.Fragment key={`ysec-${sec.year}`}>
                        {/* Year header */}
                        <TableRow sx={{ background: '#ddd' }} className="row-year">
                          <TableCell colSpan={3} sx={{ fontWeight: 700 }}>{sec.year}년</TableCell>
                          <TableCell colSpan={(selectedMonths.length * 2) + 4} />
                        </TableRow>

                        {/* Company groups */}
                        {sec.companies.map((cg, cidx) => {
                          const k = groupKey(sec.year, cg.company);
                          const isCollapsed = !!collapsed[k];

                          const subTotal = cg.rows.reduce((s, r) => s + r.total, 0);
                          const subPrev = cg.rows.reduce((s, r) => s + r.prevCum, 0);
                          const subYear = cg.rows.reduce((s, r) => s + r.yearCum, 0);
                          const subRemain = cg.rows.reduce((s, r) => s + r.remain, 0);

                          if (isCollapsed) {
                            // 접힘 상태는 소계 한 줄만
                            return (
                              <TableRow className="collapsed-placeholder" key={`ycg-${sec.year}-${cidx}`} sx={{ background: '#f5f5f5', cursor: 'pointer' }}
                                onClick={() => toggleGroup(k)}>
                                <TableCell colSpan={totalColSpan} sx={{ fontWeight: 700 }}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    <span>
                                      {cg.company} 소계 (계약 {fmt(subTotal)}, {sec.year - 1} 누계 {fmt(subPrev)}, {sec.year} 누계 {fmt(subYear)}, 잔액 {fmt(subRemain)})
                                    </span>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return (
                            <React.Fragment key={`ycg-${sec.year}-${cidx}`}>
                              {/* 상세 Rows */}
                              {cg.rows.map((r, ridx) => (
                                <TableRow key={`yrow-${sec.year}-${cidx}-${ridx}`}>
                                  <TableCell>{r.contractDate}</TableCell>
                                  <TableCell>{r.company}</TableCell>
                                  <TableCell>{r.site}</TableCell>
                                  <TableCell align="right">{fmt(r.total)}</TableCell>
                                  <TableCell align="right">{fmt(r.prevCum)}</TableCell>
                                  <TableCell align="right">{fmt(r.yearCum)}</TableCell>
                                  <TableCell align="right">{fmt(r.remain)}</TableCell>
                                  {selectedMonths.map(m => (
                                    <React.Fragment key={`ycell-${sec.year}-${cidx}-${ridx}-${m}`}>
                                      <TableCell align="right">{r.planned[m - 1] ? fmt(r.planned[m - 1]) : '-'}</TableCell>
                                      <TableCell align="right">{r.actuals[m - 1] ? fmt(r.actuals[m - 1]) : '-'}</TableCell>
                                    </React.Fragment>
                                  ))}
                                </TableRow>
                              ))}

                              {/* 소계 Row (마지막에 표시) */}
                              <TableRow
                                onClick={() => toggleGroup(k)}
                                className="row-subtotal"
                                sx={{ background: '#eee', cursor: 'pointer', userSelect: 'none' }}
                              >
                                <TableCell colSpan={3}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {cg.company} 소계
                                  </span>
                                </TableCell>
                                <TableCell align="right">{fmt(subTotal)}</TableCell>
                                <TableCell align="right">{fmt(subPrev)}</TableCell>
                                <TableCell align="right">{fmt(subYear)}</TableCell>
                                <TableCell align="right">{fmt(subRemain)}</TableCell>
                                {selectedMonths.map((m, i) => {
                                  const p = cg.rows.reduce((s, r) => s + (r.planned[m - 1] || 0), 0);
                                  const a = cg.rows.reduce((s, r) => s + (r.actuals[m - 1] || 0), 0);
                                  return (
                                    <React.Fragment key={`ycsub-${sec.year}-${cidx}-${i}`}>
                                      <TableCell align="right">{p ? fmt(p) : '-'}</TableCell>
                                      <TableCell align="right">{a ? fmt(a) : '-'}</TableCell>
                                    </React.Fragment>
                                  );
                                })}
                              </TableRow>
                            </React.Fragment>
                          );
                        })}

                        {/* year subtotal */}
                        <TableRow sx={{ background: '#e6d8a8', fontWeight: 700 }}>
                          <TableCell colSpan={3}>{sec.year}년 소계</TableCell>
                          <TableCell align="right">{fmt(sec.totals.total)}</TableCell>
                          <TableCell align="right">{fmt(sec.totals.prevCum)}</TableCell>
                          <TableCell align="right">{fmt(sec.totals.yearCum)}</TableCell>
                          <TableCell align="right">{fmt(sec.totals.remain)}</TableCell>
                          {/* 월 영역은 침범하지 않도록 합치기 */}
                          <TableCell colSpan={selectedMonths.length * 2} />
                        </TableRow>
                      </React.Fragment>
                    ))}

                    {/* grand total */}
                    <TableRow className="row-grand" sx={{ background: '#ffe599', fontWeight: 700 }}>
                      <TableCell colSpan={3}>합계</TableCell>
                      <TableCell align="right">{fmt(yearView.grand.total)}</TableCell>
                      <TableCell align="right">{fmt(yearView.grand.prevCum)}</TableCell>
                      <TableCell align="right">{fmt(yearView.grand.yearCum)}</TableCell>
                      <TableCell align="right">{fmt(yearView.grand.remain)}</TableCell>
                      {selectedMonths.map((m, i) => (
                        <React.Fragment key={`ygrand-${i}`}>
                          <TableCell align="right">{yearView.grand.planned[m - 1] ? fmt(yearView.grand.planned[m - 1]) : '-'}</TableCell>
                          <TableCell align="right">{yearView.grand.actuals[m - 1] ? fmt(yearView.grand.actuals[m - 1]) : '-'}</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </>
                )}

                {/* RANGE MODE */}
                {mode === 'RANGE' && (
                  <>
                    {rangeView.sections.map(sec => (
                      <React.Fragment key={`rsec-${sec.year}`}>
                        {/* 연도 헤더 */}
                        <TableRow sx={{ background: '#ddd' }} className="row-year">
                          <TableCell colSpan={3} sx={{ fontWeight: 700 }}>{sec.year}년</TableCell>
                          <TableCell colSpan={(rangeView.rms.length * 2) + 3} />
                        </TableRow>

                        {/* 업체 그룹 */}
                        {sec.companies.map((cg, cidx) => {
                          const k = groupKey(sec.year, cg.company);
                          const isCollapsed = !!collapsed[k];
                          const S = cg.subtotal;

                          if (isCollapsed) {
                            return (
                              <TableRow className="collapsed-placeholder" key={`rcg-${sec.year}-${cidx}`} sx={{ background: '#f5f5f5', cursor: 'pointer' }}
                                onClick={() => toggleGroup(k)}>
                                <TableCell colSpan={totalColSpan} sx={{ fontWeight: 700 }}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    <span>{cg.company} 소계 (계약 {fmt(S.total)}, 기간 누계 {fmt(S.actual)}, 잔액 {fmt(S.remain)})</span>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return (
                            <React.Fragment key={`rcg-${sec.year}-${cidx}`}>
                              {/* 상세 Rows (먼저 출력) */}
                              {cg.rows.map((r, ridx) => (
                                <TableRow key={`rrow-${sec.year}-${cidx}-${ridx}`}>
                                  <TableCell>{r.contractDate}</TableCell>
                                  <TableCell>{cg.company}</TableCell>
                                  <TableCell>{r.site}</TableCell>
                                  <TableCell align="right">{fmt(r.total)}</TableCell>
                                  <TableCell align="right">{fmt(r.actualInRange)}</TableCell>
                                  <TableCell align="right">{fmt(r.remainAtEnd)}</TableCell>
                                  {rangeView.rms.map((_, i) => (
                                    <React.Fragment key={`rcell-${sec.year}-${cidx}-${ridx}-${i}`}>
                                      <TableCell align="right">{r.planned[i] ? fmt(r.planned[i]) : '-'}</TableCell>
                                      <TableCell align="right">{r.actuals[i] ? fmt(r.actuals[i]) : '-'}</TableCell>
                                    </React.Fragment>
                                  ))}
                                </TableRow>
                              ))}

                              {/* 업체 소계 (마지막에 출력) */}
                              <TableRow
                                onClick={() => toggleGroup(k)}
                                className="row-subtotal"
                                sx={{ background: '#eee', cursor: 'pointer', userSelect: 'none' }}
                              >
                                <TableCell colSpan={3}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {cg.company} 소계
                                  </span>
                                </TableCell>
                                <TableCell align="right">{fmt(S.total)}</TableCell>
                                <TableCell align="right">{fmt(S.actual)}</TableCell>
                                <TableCell align="right">{fmt(S.remain)}</TableCell>
                                {rangeView.rms.map((_, i) => (
                                  <React.Fragment key={`rcsub-${sec.year}-${cidx}-${i}`}>
                                    <TableCell align="right">{S.planned[i] ? fmt(S.planned[i]) : '-'}</TableCell>
                                    <TableCell align="right">{S.actuals[i] ? fmt(S.actuals[i]) : '-'}</TableCell>
                                  </React.Fragment>
                                ))}
                              </TableRow>
                            </React.Fragment>
                          );
                        })}

                        {/* 연도 소계 */}
                        <TableRow sx={{ background: '#e6d8a8', fontWeight: 700 }}>
                          <TableCell colSpan={3}>{sec.year}년 소계</TableCell>
                          <TableCell align="right">{fmt(sec.totals.total)}</TableCell>
                          <TableCell align="right">{fmt(sec.totals.actual)}</TableCell>
                          <TableCell align="right">{fmt(sec.totals.remain)}</TableCell>
                          {rangeView.rms.map((_, i) => (
                            <React.Fragment key={`ryst-${sec.year}-${i}`}>
                              <TableCell align="right">{sec.totals.planned[i] ? fmt(sec.totals.planned[i]) : '-'}</TableCell>
                              <TableCell align="right">{sec.totals.actuals[i] ? fmt(sec.totals.actuals[i]) : '-'}</TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))}

                    {/* 전체 합계 */}
                    {(() => {
                      const grandPlanned = Array(rangeView.rms.length).fill(0);
                      const grandActuals = Array(rangeView.rms.length).fill(0);
                      rangeView.sections.forEach(sec => {
                        for (let i = 0; i < rangeView.rms.length; i++) {
                          grandPlanned[i] += sec.totals.planned[i] || 0;
                          grandActuals[i] += sec.totals.actuals[i] || 0;
                        }
                      });

                      return (
                        <TableRow className="row-grand" sx={{ background: '#ffe599', fontWeight: 700 }}>
                          <TableCell colSpan={3}>합계</TableCell>
                          <TableCell align="right">{fmt(rangeView.grand.total)}</TableCell>
                          <TableCell align="right">{fmt(rangeView.grand.actual)}</TableCell>
                          <TableCell align="right">{fmt(rangeView.grand.remain)}</TableCell>
                          {rangeView.rms.map((_, i) => (
                            <React.Fragment key={`rgrand-${i}`}>
                              <TableCell align="right">{grandPlanned[i] ? fmt(grandPlanned[i]) : '-'}</TableCell>
                              <TableCell align="right">{grandActuals[i] ? fmt(grandActuals[i]) : '-'}</TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      );
                    })()}
                  </>
                )}

              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default SalesPlanning;
